import React, { useRef, useEffect } from "react";
import { Box, Typography, Button, Container, useMediaQuery } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import { getBlogs } from "../../api/services";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const IMAGE_DOMAIN = "https://dprstorage.b-cdn.net";

gsap.registerPlugin(ScrollTrigger);

const BlogSection: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const sectionRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error } = useQuery(['blogs-home'], getBlogs);

  let blogs: any[] = [];
  if (data && Array.isArray(data.blogs)) {
    blogs = data.blogs.slice(0, 2);
  }

  useEffect(() => {
    const el = sectionRef.current;
    if (el) {
      gsap.fromTo(
        el,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 87%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }
    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Blogs | Kolla Group</title>
        <meta name="description" content="Read the latest blogs from Kolla Group about luxury living, sustainability, and upcoming projects." />
        <meta name="keywords" content="Kolla Group, Blogs, Luxury Living, Sustainability, Hyderabad Projects" />
      </Helmet>
      <Box ref={sectionRef} sx={{ px: { xs: 0, md: 4 }, py: { xs: 3, md: 4 }}}>
        <Container maxWidth={isMobile ? false : "xl"}>
          <Box sx={{ textAlign: "left", mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: "600",
                fontSize: "28px",
                background: "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                fontFamily: "LEMON MILK",
                mb: 4,
              }}
            >
              Blogs
            </Typography>
          </Box>
          <Container maxWidth={isMobile ? false : "xl"}>
            {isLoading && <Typography color="text.secondary" sx={{ mb: 4 }}>Loading...</Typography>}
            {isError && <Typography color="error" sx={{ mb: 4 }}>Error: {(error as Error)?.message}</Typography>}
            {!isLoading && !isError && blogs.map((blog) => {
              const handleNavigate = () => {
                if (blog.slug) navigate(`/blogs/${blog.slug}`);
              };
              return (
                <Box
                  key={blog._id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    mb: 4,
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                    background: "#181818",
                    minHeight: 220,
                  }}
                >
                  {/* Image Section */}
                  <Box
                    sx={{
                      width: { xs: "100%", md: "35%" },
                      height: { xs: 180, md: "auto" },
                      minHeight: { md: 220 },
                      maxHeight: { md: 300 },
                      flexShrink: 0,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#181818",
                      cursor: "pointer",
                      m:2,
                    }}
                    onClick={handleNavigate}
                  >
                    <Box
                      component="img"
                      src={blog.imageurl?.startsWith('http') ? blog.imageurl : IMAGE_DOMAIN + blog.imageurl}
                      alt={blog.title}
                      sx={{
                        width: "100%",
                        height: "98%",
                        objectFit: "cover",
                        objectPosition: "center",
                        background: "#181818",
                        display: "block",
                        borderRadius: "10px"
                      }}
                    />
                  </Box>
                  {/* Content Section */}
                  <Box
                    sx={{
                      width: { xs: "100%", md: "65%" },
                      p: { xs: 2, md: 3 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      background: "#181818",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: "600",
                        fontSize: "24px",
                        background: "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        display: "inline-block",
                        fontFamily: "LEMON MILK",
                        mb: 4,
                        cursor: "pointer",
                      }}
                      onClick={handleNavigate}
                    >
                      {blog.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#cfcfcf",
                        mb: 2,
                        fontSize: { xs: "14px", md: "16px" },
                        lineHeight: 1.7,
                      }}
                    >
                      {(blog.metaDescription || blog.sections?.[0]?.description || blog.description || "")}
                    </Typography>
                    {/* Push the button to the bottom */}
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          alignSelf: "flex-end",
                          background: "linear-gradient(90deg, #9f8054 60%, #bf742c 100%)",
                          color: "#fff",
                          fontWeight: 700,
                          borderRadius: "20px",
                          px: 3,
                          py: 1,
                          textTransform: "uppercase",
                          fontSize: "14px",
                          letterSpacing: 1,
                          boxShadow: "0 4px 16px rgba(159,128,84,0.18)",
                          "&:hover": {
                            background: "linear-gradient(90deg, #bf742c 60%, #9f8054 100%)",
                          },
                        }}
                        onClick={handleNavigate}
                      >
                        Read More
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {/* Read More Blogs Button */}
            <Box textAlign="center" mt={2}>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16, ml: 0.5 }} />}
                sx={{
                  borderRadius: "16px",
                  px: 2.5,
                  py: 0.5,
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: 0.5,
                  borderWidth: 2,
                  borderColor: "#9f8054",
                  color: "#9f8054",
                  minHeight: "32px",
                  lineHeight: 1.2,
                  "& .MuiButton-endIcon": {
                    marginLeft: "6px",
                  },
                  "&:hover": {
                    background: "#9f8054",
                    color: "#fff",
                    borderColor: "#9f8054",
                  },
                }}
                onClick={() => navigate("/blogs")}
              >
                Read More Blogs
              </Button>
            </Box>
          </Container>
        </Container> 
      </Box>   
    </>
  );
};

export default BlogSection;